import { DataGrid, DataGridProps, GridColDef, GridPaginationModel, GridRowIdGetter, GridValidRowModel } from "@mui/x-data-grid";
import React from "react";
import { useTranslation } from "react-i18next";
import { PrimaryColors, Shades } from "../../style/Colors";

interface ICustomTableProps<R extends object> {
  columns: GridColDef[];
  rows: R[];
  loading?: boolean;
  getRowId?: (row: R) => string | number;
  onRowClick?: (row: R) => void;
  getRowClassName?: (row: R) => string;
  paginationModel?: GridPaginationModel;
  onPaginationModelChange?: (model: GridPaginationModel) => void;
  pageSizeOptions?: number[];
  rowCount?: number;
  paginationMode?: DataGridProps["paginationMode"];
  slots?: DataGridProps["slots"];
  slotProps?: DataGridProps["slotProps"];
  sx?: DataGridProps["sx"];
  /** Replaces the generic "no data" message. */
  noRowsLabel?: string;
}

export const CustomTable = <R extends object>(props: ICustomTableProps<R>): React.ReactElement => {
  const {
    columns,
    rows,
    loading,
    getRowId,
    onRowClick,
    getRowClassName,
    paginationModel,
    onPaginationModelChange,
    pageSizeOptions,
    rowCount,
    paginationMode,
    slots,
    slotProps,
    sx,
    noRowsLabel,
  } = props;
  const { t } = useTranslation();

  const centeredColumns: GridColDef[] = columns.map((col) => ({
    headerAlign: "center",
    align: "center",
    ...col,
  }));

  const rowIdGetter: GridRowIdGetter | undefined = getRowId
    ? (row: GridValidRowModel) => getRowId(row as unknown as R)
    : undefined;

  return (
    <DataGrid
      rows={rows as GridValidRowModel[]}
      columns={centeredColumns}
      loading={loading}
      autoHeight
      disableColumnResize
      disableColumnMenu
      disableRowSelectionOnClick
      getRowId={rowIdGetter}
      onRowClick={onRowClick ? (params) => onRowClick(params.row as unknown as R) : undefined}
      getRowClassName={getRowClassName ? (params) => getRowClassName(params.row as unknown as R) : undefined}
      paginationModel={paginationModel}
      onPaginationModelChange={onPaginationModelChange}
      pageSizeOptions={pageSizeOptions ?? [50, 100]}
      rowCount={rowCount}
      paginationMode={paginationMode}
      slots={slots}
      localeText={{
        noRowsLabel: noRowsLabel ?? t("table.body.emptyDataSourceMessage"),
        footerRowSelected: (count: number) => t("table.nRowsSelected", { 0: count }),
        toolbarQuickFilterPlaceholder: t("table.toolbar.searchPlaceholder"),
        MuiTablePagination: {
          labelRowsPerPage: t("table.labelRowsPerPage"),
          labelDisplayedRows: ({ from, to, count }: { from: number; to: number; count: number }) =>
            t("table.labelDisplayedRows", { from, to, count }),
          getItemAriaLabel: (type: string) => {
            if (type === "first") return t("table.firstTooltip");
            if (type === "last") return t("table.lastTooltip");
            if (type === "next") return t("table.nextTooltip");
            return t("table.previousTooltip");
          },
        },
      }}
      slotProps={{
        ...slotProps,
        pagination: {
          ...(slotProps?.pagination as object),
          showFirstButton: true,
          showLastButton: true,
        },
      }}
      sx={{
        width: "100%",
        border: "none",
        boxShadow: "none",
        "& .MuiDataGrid-columnHeader": {
          backgroundColor: PrimaryColors.base,
          color: Shades.white,
          fontWeight: "bold",
          fontSize: 16,
          height: "60px !important",
        },
        "& .MuiDataGrid-columnHeaderTitle": {
          fontWeight: "bold",
        },
        "& .MuiDataGrid-columnSeparator": {
          display: "none",
        },
        "& .MuiDataGrid-cell": {
          fontSize: 16,
        },
        "& .MuiDataGrid-footerContainer": {
          backgroundColor: PrimaryColors.base,
          minHeight: "52px",
          alignItems: "center",
        },
        "& .MuiTablePagination-root": {
          overflow: "visible",
        },
        "& .MuiTablePagination-toolbar": {
          alignItems: "center",
          minHeight: "52px",
          paddingLeft: "16px",
          paddingRight: "4px",
        },
        "& .MuiTablePagination-selectLabel": {
          margin: 0,
        },
        "& .MuiTablePagination-displayedRows": {
          margin: 0,
        },
        "& .MuiDataGrid-footerContainer .MuiInputBase-root": {
          height: "auto !important",
          minHeight: "unset !important",
          backgroundColor: "transparent !important",
          borderRadius: "0 !important",
          boxShadow: "none !important",
          border: "none !important",
          padding: "0 !important",
        },
        "& .MuiDataGrid-footerContainer .MuiInputBase-input": {
          padding: "0 24px 0 4px !important",
        },
        "& .MuiTablePagination-actions .MuiIconButton-root": {
          padding: "4px",
        },
        "& .MuiTablePagination-actions .MuiIconButton-root.Mui-disabled": {
          opacity: 0.4,
        },
        ...sx,
      }}
    />
  );
};
